import { ref } from 'vue';
import { axios } from '@/plugins/axios';
import qs from 'qs';

export function useApiResource() {
    const endpoint = ref('');
    const mapFn = ref(null);
    const transformFn = ref(null);
    const isLoading = ref(false);
    const data = ref([]);
    const error = ref(null);
    const onErrorCallback = ref(null);

    const filters = ref({});
    const sorts = ref([]);
    const fields = ref({});
    const includes = ref([]);

    const buildQuery = () => {
        const query = {};

        if (Object.keys(filters.value).length > 0) {
            query.filter = filters.value;
        }

        if (sorts.value.length > 0) {
            query.sort = sorts.value.join(',');
        }

        if (Object.keys(fields.value).length > 0) {
            query.fields = fields.value;
        }

        if (includes.value.length > 0) {
            query.include = includes.value.join(',');
        }

        return qs.stringify(query, { encode: false });
    };

    const load = async () => {
        if (!endpoint.value) {
            throw new Error('No endpoint defined in useResource()');
        }

        isLoading.value = true;
        error.value = null;

        try {
            const queryString = buildQuery();
            const url = queryString ? `${endpoint.value}?${queryString}` : endpoint.value;

            const response = await axios.get(url);
            let raw = response.data ?? [];
            raw = transformFn.value ? transformFn.value(raw) : raw;
            data.value = mapFn.value ? raw.map(mapFn.value) : raw;
        } catch (e) {
            error.value = e;
            onErrorCallback.value?.(e);
        } finally {
            isLoading.value = false;
        }
    };

    const builder = {
        endpoint(url) {
            endpoint.value = url;
            return builder;
        },
        filter(value) {
            filters.value = value;
            return builder;
        },
        sort(...fields) {
            sorts.value.push(...fields);
            return builder;
        },
        fields(fieldList) {
            fields.value = fieldList;
            return builder;
        },
        include(...relations) {
            includes.value.push(...relations);
            return builder;
        },
        transform(fn) {
            transformFn.value = fn;
            return builder;
        },
        map(fn) {
            mapFn.value = fn;
            return builder;
        },
        onError(fn) {
            onErrorCallback.value = fn;
            return builder;
        },
        load,
        get data() {
            return data;
        },
        get isLoading() {
            return isLoading;
        },
        get error() {
            return error;
        },
    };

    return builder;
}
